import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { attribution, track } from "@/lib/analytics";

export const TestDriveForm = () => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", province: "", postal_code: "",
    city: "", notes: "", wait_3_months: true, privacy_consent: false, marketing_consent: false,
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  useEffect(() => {
    track("test_drive_start", {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone) return toast.error("Nome e telefono sono obbligatori.");
    if (!form.privacy_consent) return toast.error("È necessario accettare la privacy policy.");
    setLoading(true);
    try {
      const deliveryNote = "Consegna: opzione in produzione (3 mesi) a €5.490 + IVA + trasporto. Consegna rapida esaurita.";
      const notes = [form.notes, deliveryNote].filter(Boolean).join("\n");
      await api.post("/test-drive", {
        ...form, notes, region: "", lead_type: "test_drive", ...attribution(),
      });
      track("test_drive_request", { wait_3_months: form.wait_3_months });
      track("lead_form_submit", { form: "test_drive" });
      setSent(true);
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <div className="rounded-3xl bg-emerald-50 border border-emerald-200 p-10 text-center" data-testid="testdrive-success">
      <CheckCircle2 className="mx-auto text-emerald-700 mb-3" size={56} />
      <h3 className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>Richiesta inviata!</h3>
      <p className="text-emerald-800/80 mt-2 text-lg">Grazie {form.first_name}. Verrai contattato dall'azienda per procedere con la conferma dell'ordine.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-200 shadow-md p-6 sm:p-8 space-y-4" data-testid="test-drive-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-semibold text-slate-700">Nome *</label><Input value={form.first_name} onChange={set("first_name")} className="h-12 mt-1" data-testid="td-first-name" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Cognome</label><Input value={form.last_name} onChange={set("last_name")} className="h-12 mt-1" data-testid="td-last-name" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Telefono *</label><Input value={form.phone} onChange={set("phone")} className="h-12 mt-1" data-testid="td-phone" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Email</label><Input value={form.email} onChange={set("email")} className="h-12 mt-1" data-testid="td-email" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Provincia</label><Input value={form.province} onChange={set("province")} className="h-12 mt-1" data-testid="td-province" /></div>
        <div><label className="text-sm font-semibold text-slate-700">CAP</label><Input value={form.postal_code} onChange={set("postal_code")} className="h-12 mt-1" data-testid="td-postal" /></div>
        <div className="sm:col-span-2"><label className="text-sm font-semibold text-slate-700">Città</label><Input value={form.city} onChange={set("city")} className="h-12 mt-1" data-testid="td-city" /></div>
      </div>
      <div><label className="text-sm font-semibold text-slate-700">Note</label><Textarea value={form.notes} onChange={set("notes")} className="mt-1" rows={3} data-testid="td-notes" /></div>
      <div className="space-y-2" data-testid="td-delivery-options">
        <p className="text-sm font-semibold text-slate-700">Opzione di consegna</p>
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-100 p-4 text-sm text-slate-500 opacity-70 cursor-not-allowed" aria-disabled="true" data-testid="td-option-ready">
          <span className="flex items-center gap-2"><Checkbox disabled checked={false} /> <span className="line-through">Consegna in 8 giorni · €5.990 + IVA + trasporto</span></span>
          <span className="shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold uppercase px-2.5 py-1">Esaurito</span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="td-option-production">
          <span className="flex items-center gap-2"><Checkbox checked disabled className="disabled:opacity-100" data-testid="td-wait-3-months" /> <span>Consegna in 3 mesi · <strong>€5.490</strong> + IVA + trasporto</span></span>
          <span className="shrink-0 rounded-full bg-emerald-600 text-white text-xs font-bold uppercase px-2.5 py-1">Disponibile</span>
        </div>
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-600">
        <Checkbox checked={form.privacy_consent} onCheckedChange={(v) => setForm({ ...form, privacy_consent: !!v })} className="mt-0.5" data-testid="td-privacy" />
        <span>Ho letto e accetto la <a href="/privacy" className="underline">Privacy Policy</a>. *</span>
      </label>
      <label className="flex items-start gap-2 text-sm text-slate-600">
        <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })} className="mt-0.5" data-testid="td-marketing" />
        <span>Acconsento a ricevere comunicazioni commerciali (facoltativo).</span>
      </label>
      <Button type="submit" disabled={loading} className="w-full h-14 text-lg font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="td-submit">
        {loading ? "Invio in corso..." : "PRENOTA LA TUA SENIORCAR"}
      </Button>
      <p className="text-center text-xs text-slate-500">Nessun pagamento ora · Ti ricontattiamo per completare l'ordine con l'azienda</p>
    </form>
  );
};
